import React, { useState, useEffect, useImperativeHandle, useRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { spacing8, spacing16, spacing24, grey30, info40, blue40, danger40, confirmation40, highlightCyan } from "@churchofjesuschrist/eden-style-constants";
import { Secondary, Icon } from "@churchofjesuschrist/eden-buttons";
import { Add, Done, LoadingSpinner, Alert, Clear } from "@churchofjesuschrist/eden-icons";
import Row from "@churchofjesuschrist/eden-row";
const FileInfoContainer = styled.div.withConfig({
  componentId: "sc-15by3kn-0"
})(["display:inline-block;margin-block-end:", ";"], spacing8);
export const InternalUseOnlyFileInput = styled.div.withConfig({
  componentId: "sc-15by3kn-1"
})(["display:flex;align-items:flex-start;flex-direction:column-reverse;"]);
const AddIcon = styled(Add).withConfig({
  componentId: "sc-15by3kn-2"
})(["pointer-events:none;visibility:hidden;"]);
const DropZone = styled.div.withConfig({
  componentId: "sc-15by3kn-3"
})(["position:relative;", ";width:100%;", ";"], _ref => {
  let {
    isDroppable
  } = _ref;
  return isDroppable ? `padding: ${spacing16} ${spacing8};
  border: 1px dashed ${grey30};` : ``;
}, _ref2 => {
  let {
    isDragActive
  } = _ref2;
  return isDragActive ? `
    display: flex;
    align-items: center;
    justify-content: center;
    border-color: ${blue40}; 
    background: ${highlightCyan};
    & > * {
        visibility: hidden;
    }
    & > ${AddIcon} {
        visibility: visible;
    }
    ` : "";
});
const isValidFile = (file, accept) => {
  if (accept) {
    const extension = file.name.split(".").pop();
    const type = file.type;
    const joinedAccept = accept.split(",").map(item => item.trim());
    let isValid = false;
    joinedAccept.forEach(acceptValue => {
      if (acceptValue === `.${extension}`) {
        isValid = true;
      } else if (acceptValue === type) {
        isValid = true;
      } else {
        const splitAcceptValue = acceptValue.split("/");
        if (splitAcceptValue[1] === "*" && type.includes(splitAcceptValue[0])) {
          isValid = true;
        }
      }
    });
    return isValid;
  } else {
    return true;
  }
};
const convertValueToObject = (value, fileType) => {
  return value.flatMap(file => file ? {
    name: file.name,
    lastModified: file.lastModified,
    size: file.size,
    type: file.type,
    lastModifiedDate: file.lastModifiedDate,
    base64String: fileType === "file" ? "use the file object instead" : file.base64String,
    file: fileType === "file" ? file.file : {}
  } : []);
};

/**
 * The FileInput allows the user to choose one or more files from their device's storage.
 */
const FileInput = /*#__PURE__*/React.forwardRef((_ref3, forwardedRef) => {
  let {
    defaultValue = [],
    onChange = x => x,
    fileType = "base64",
    capture,
    accept,
    children,
    required,
    isDroppable,
    multiple,
    name,
    value = defaultValue,
    ...props
  } = _ref3;
  const [isDragActive, setIsDragActive] = useState(false);
  let convertedValue = convertValueToObject(value, fileType);
  convertedValue = !multiple && convertedValue.length > 1 ? [convertedValue[0]] : convertedValue;
  const [innerValue, setInnerValue] = useState(convertedValue);
  const stringifyConvertedValue = JSON.stringify(convertedValue);
  const ref = useRef();
  useImperativeHandle(forwardedRef, () => ref.current);
  useEffect(() => {
    setInnerValue(convertedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringifyConvertedValue]);
  useEffect(() => {
    const closestForm = ref.current.form;
    if (!closestForm) {
      return;
    }
    const resetHandler = () => {
      setInnerValue(convertedValue);
    };
    closestForm.addEventListener("reset", resetHandler);
    return () => closestForm.removeEventListener("reset", resetHandler);
  });
  useEffect(() => {
    ref.current._value = ref.current.value;
    Object.defineProperty(ref.current, "value", {
      get: function () {
        return this._value;
      },
      set: function (v) {
        if (v === "") {
          setInnerValue([]);
          this._value = "";
          const parentNode = ref.current.parentNode;
          const form = document.createElement("form");
          form.appendChild(ref.current);
          form.reset();
          //Currently the input is the last child of the DropZone component.  If that changes, the next line will need to be updated.
          parentNode.appendChild(ref.current);
          form.remove();
        } else {
          console.error("Uncaught InvalidStateError: Failed to set the 'value' property on 'HTMLInputElement': This input element accepts a filename, which may only be programmatically set to the empty string.");
        }
      }
    });
  }, []);
  const handleInputSelect = () => {
    ref.current.click();
  };
  const mockEvent = newValue => ({
    target: {
      value: newValue,
      name: name
    }
  });
  const removeFromValue = (fileName, event) => {
    event.preventDefault();
    event.stopPropagation();
    const clone = [...innerValue];
    const index = clone.findIndex(_ref4 => {
      let {
        name
      } = _ref4;
      return name === fileName;
    });
    clone.splice(index, 1);
    if ((clone === null || clone === void 0 ? void 0 : clone.length) === 0) {
      ref.current.value = "";
    }
    if (!clone.find(item => item.base64String.startsWith("error:"))) {
      ref.current.setCustomValidity("");
    }
    setInnerValue(clone);
    onChange(mockEvent(clone));
  };
  const changeHandler = event => {
    const files = event.target.files;
    const promises = [...files].map(file => {
      return new Promise(resolve => {
        if (!innerValue.find(elem => elem.name === file.name)) {
          if (isValidFile(file, accept)) {
            const reader = new FileReader();
            const newFile = {
              name: file.name,
              lastModified: file.lastModified,
              size: file.size,
              type: file.type,
              lastModifiedDate: file.lastModifiedDate,
              base64String: "",
              file: fileType === "file" ? file : {}
            };
            if (fileType === "file") {
              resolve({
                ...newFile,
                base64String: "use the file object instead"
              });
            } else {
              reader.readAsDataURL(file);
              reader.onload = () => {
                resolve({
                  ...newFile,
                  base64String: reader.result
                });
              };
              reader.onerror = () => {
                resolve({
                  ...newFile,
                  base64String: "error: Unknown"
                });
                ref.current.setCustomValidity("Unknown Error");
              };
              reader.onloadend = () => {
                if (!reader.result) {
                  resolve({
                    ...newFile,
                    base64String: "error: File too Large"
                  });
                  ref.current.setCustomValidity("File too Large");
                }
              };
            }
          } else {
            resolve({
              name: file.name,
              base64String: "error: incompatible file type"
            });
            ref.current.setCustomValidity("Incompatible File Type");
          }
        }
      });
    });
    Promise.all(promises).then(function () {
      let newValue = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
      if (multiple) {
        newValue = [...innerValue, ...newValue];
      } else {
        newValue = newValue.length > 0 ? [newValue[0]] : [];
      }
      setInnerValue(newValue);
      onChange(mockEvent(newValue));
      ref.current._value = newValue[0] ? `C:\\fakepath\\${newValue[0].name}` : "";
    });
  };
  const handleDrop = event => {
    if (isDroppable) {
      event.preventDefault();
      setIsDragActive(false);
      ref.current.files = event.dataTransfer.files;
      changeHandler({
        target: ref.current
      });
    }
  };
  return /*#__PURE__*/React.createElement(InternalUseOnlyFileInput, props, /*#__PURE__*/React.createElement(DropZone, {
    isDroppable: isDroppable,
    isDragActive: isDragActive,
    onDrop: handleDrop,
    onDragEnter: isDroppable ? event => {
      event.preventDefault();
      setIsDragActive(true);
    } : x => x,
    onDragOver: event => event.preventDefault(),
    onDragLeave: isDroppable ? () => {
      setIsDragActive(false);
    } : x => x
  }, /*#__PURE__*/React.createElement(Secondary, {
    onClick: handleInputSelect
  }, children), /*#__PURE__*/React.createElement(AddIcon, {
    size: spacing24
  }), /*#__PURE__*/React.createElement("input", {
    style: {
      opacity: 0,
      width: 0,
      height: 0
    },
    ref: ref,
    type: "file",
    accept: accept,
    capture: capture,
    onChange: event => {
      event.stopPropagation();
      changeHandler(event);
    },
    required: required,
    name: name,
    multiple: multiple,
    tabIndex: -1
  })), innerValue !== null && innerValue !== void 0 && innerValue.length ? /*#__PURE__*/React.createElement(FileInfoContainer, null, innerValue.map(_ref5 => {
    let {
      name,
      base64String
    } = _ref5;
    return /*#__PURE__*/React.createElement(Row, {
      key: name,
      style: {
        flexWrap: "nowrap"
      },
      verticalAlign: "center"
    }, /*#__PURE__*/React.createElement("div", null, base64String ? base64String.startsWith("error:") ? /*#__PURE__*/React.createElement(Alert, {
      size: spacing24,
      color: danger40
    }) : /*#__PURE__*/React.createElement(Done, {
      size: spacing24,
      color: confirmation40
    }) : /*#__PURE__*/React.createElement(LoadingSpinner, {
      size: spacing24,
      color: info40
    })), /*#__PURE__*/React.createElement("div", {
      style: {
        width: "100%"
      }
    }, name, " "), /*#__PURE__*/React.createElement(Icon, {
      onClick: event => removeFromValue(name, event),
      size: "small"
    }, /*#__PURE__*/React.createElement(Clear, {
      size: spacing24
    })));
  })) : null);
});
FileInput.displayName = "FileInput";
FileInput.propTypes = {
  /** Content to render inside the FileInput Button Component */
  children: PropTypes.node.isRequired,
  /** One or more unique file type specifiers describing file types to allow [docs](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#Unique_file_type_specifiers) */
  accept: PropTypes.string,
  /** What source to use for capturing image or video data */
  capture: PropTypes.oneOf(["user", "environment", ""]),
  /** Whether the FileInput needs at least one file to be considered valid */
  required: PropTypes.bool,
  /** An array of values for the FileInput
   * ```js
   *    PropTypes.shape({
   *      name: PropTypes.string,
   *      lastModified: PropTypes.number,
   *      size: PropTypes.number,
   *      type: PropTypes.string,
   *      lastModifiedDate: PropTypes.instanceOf(Date),
   *      base64String: PropTypes.string,
   *      file: PropTypes.object
   *    })
   * ```
   */
  value: PropTypes.array,
  /** Default value for uncontrolled use
   * ```js
   *    PropTypes.shape({
   *      name: PropTypes.string,
   *      lastModified: PropTypes.number,
   *      size: PropTypes.number,
   *      type: PropTypes.string,
   *      lastModifiedDate: PropTypes.instanceOf(Date),
   *      base64String: PropTypes.string,
   *      file: PropTypes.object
   *    })
   * ```
   */
  defaultValue: PropTypes.array,
  /** Used to reference the FileInput in JavaScript, or to reference form data after a form is submitted */
  name: PropTypes.string,
  /** Callback to execute when the value changes */
  onChange: PropTypes.func,
  /** Enables drag and drop capabilities */
  isDroppable: PropTypes.bool,
  /** Enables multiple file upload capabilities */
  multiple: PropTypes.bool,
  /** Determines how the file will be returned in the value */
  fileType: PropTypes.oneOf(["base64", "file"])
};
export default FileInput;